import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import ToggleButton from "src/components/common/Buttons/ToggleButton";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import FormFilterInput from "src/components/form/inputs/FormFilterInput/FormFilterInput";
import { PersonnelByRoleBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PersonnelActions from "../../../actions/personnel";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";

import "./PersonnelByRolesPage.scss";

interface PersonnelByRolesPageProps {
    rolePersonnelGrouping: Dtos.RolePersonnelGrouping[];
    formFilter: IFormFilter;
    loadingPersonnel: boolean;
    loadPersonnelSuccess: boolean;
    loadPersonnelFailure: boolean;
    loadPersonnelErrorCode: string;
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    loadPersonnelByRole: PersonnelActions.IPersonnelByRoleLoadActionCreator;
    clearPersonnel: PersonnelActions.IPersonnelClearActionCreator;
    filter: PersonnelActions.IPersonnelFilterByRoleActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const PersonnelByRolesPage: FC<PersonnelByRolesPageProps> = ({
    rolePersonnelGrouping,
    formFilter,
    loadingPersonnel,
    loadPersonnelSuccess,
    loadPersonnelFailure,
    loadPersonnelErrorCode,
    permissions,
    loadPersonnelByRole,
    clearPersonnel,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- States --
    const [showCeased, setShowCeased] = useState<boolean>(false);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);
    const [init, setInit] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showPersonnelRoles =
        !loadingPersonnel && loadPersonnelSuccess && !creatingTree;


    // Load personnel by role
    useEffect(() => {
        if (!init && !loadingPersonnel) {
            setDrawerItems([]);
            setBreadcrumbs(PersonnelByRoleBreadcrumbs());

            setTreeData(null);

            loadPersonnelByRole();

            setInit(true);
        }
    }, [
        init,
        setInit,
        loadingPersonnel,
        setDrawerItems,
        setBreadcrumbs,
        PersonnelByRoleBreadcrumbs,
        setTreeData,
        loadPersonnelByRole,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadPersonnelFailure) {
            navigate(`/error/${loadPersonnelErrorCode}`);
            clearPersonnel();
        }
    }, [
        loadPersonnelFailure,
        loadPersonnelErrorCode,
        navigate,
        clearPersonnel,
    ]);

    // Cleanup effect to clear the personnel on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearPersonnel();
        };
    }, [clearPersonnel, setTreeData]);

    // Update tree
    useEffect(() => {
        setTreeData(null);
        setInitalizedTreeData(false);
    }, [rolePersonnelGrouping, showCeased, setInitalizedTreeData, setTreeData]);

    const onChange = (newValue) => {
        setShowCeased(newValue);
        setCreatingTree(true);
    };

    // Create tree
    useEffect(() => {
        if (rolePersonnelGrouping && !initalizedTreeData) {
            setCreatingTree(true);

            // If no personnel by role return
            if (!rolePersonnelGrouping) {
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (rolePersonnels, showCeased) => {
                let children = [];

                if (showCeased) {
                    children =
                        rolePersonnels
                            ?.sort((a, b) => a?.id - b?.id)
                            ?.map((y) => ({
                                id: `${y?.roleId}-${y?.trialId}-${y?.institutionId}-${y?.dateStarted}-${y?.dateCeased}-personnel-${y.personnelId}`,
                                data: (
                                    <div
                                        className={`${
                                            y?.dateCeased ? "ceased" : ""
                                        }`}
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                "1fr 3fr 1fr 4fr 3fr",
                                        }}
                                    >
                                        <div className="px-1">
                                            <FontAwesomeIcon
                                                icon={
                                                    FontAwesomeIcons.Duotone
                                                        .USER
                                                }
                                            />
                                        </div>
                                        <div className="px-1">
                                            {`${y?.firstName} ${y?.lastName}`}
                                        </div>
                                        <div className="px-1">
                                            {y?.isGlobal ? (
                                                <FontAwesomeIcon
                                                    icon={
                                                        FontAwesomeIcons.Duotone
                                                            .SQUARECHECK
                                                    }
                                                />
                                            ) : (
                                                ""
                                            )}
                                        </div>
                                        <div className="d-inline-block px-1">
                                            <span
                                                className="text-truncate"
                                                style={{
                                                    whiteSpace: "pre-line",
                                                }}
                                            >
                                                {y?.protocolID && y?.shortTitle
                                                    ? `${y.protocolID} | ${y.shortTitle}`
                                                    : null}
                                            </span>
                                        </div>
                                        <div className="d-inline-block px-1">
                                            <span
                                                className="text-truncate"
                                                style={{
                                                    whiteSpace: "pre-line",
                                                }}
                                            >
                                                {y?.institutionName}
                                            </span>
                                        </div>
                                    </div>
                                ),
                                icon: null,
                                collapsible: false,
                            })) ?? [];
                } else {
                    children =
                        rolePersonnels
                            ?.filter((x) => !x.dateCeased)
                            ?.sort((a, b) => a?.id - b?.id)
                            ?.map((y) => ({
                                id: `${y?.roleId}-${y?.trialId}-${y?.institutionId}-${y?.dateStarted}-${y?.dateCeased}-personnel-${y.personnelId}`,
                                data: (
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                "1fr 3fr 1fr 4fr 3fr",
                                        }}
                                    >
                                        <div className="px-1">
                                            <FontAwesomeIcon
                                                icon={
                                                    FontAwesomeIcons.Duotone
                                                        .USER
                                                }
                                            />
                                        </div>
                                        <div className="px-1">
                                            {`${y?.firstName} ${y?.lastName}`}
                                        </div>
                                        <div className="px-1">
                                            {y?.isGlobal ? (
                                                <FontAwesomeIcon
                                                    icon={
                                                        FontAwesomeIcons.Duotone
                                                            .SQUARECHECK
                                                    }
                                                />
                                            ) : (
                                                ""
                                            )}
                                        </div>
                                        <div className="d-inline-block px-1">
                                            <span
                                                className="text-truncate"
                                                style={{
                                                    whiteSpace: "pre-line",
                                                }}
                                            >
                                                {y?.protocolID && y?.shortTitle
                                                    ? `${y.protocolID} | ${y.shortTitle}`
                                                    : null}
                                            </span>
                                        </div>
                                        <div className="d-inline-block px-1">
                                            <span
                                                className="text-truncate"
                                                style={{
                                                    whiteSpace: "pre-line",
                                                }}
                                            >
                                                {y?.institutionName}
                                            </span>
                                        </div>
                                    </div>
                                ),
                                icon: null,
                                collapsible: false,
                            })) ?? [];
                }

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 1fr 4fr 3fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Personnel</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Global</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Trial</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Institution</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                rolePersonnelGrouping
                    ?.sort((a, b) => a?.roleRank - b?.roleRank)
                    ?.filter((z) => {
                        if (!z?.rolePersonnels) return false;
                        if (!showCeased) {
                            return (
                                z.rolePersonnels.filter((p) => !p.dateCeased)
                                    .length > 0
                            );
                        }
                        return true;
                    })
                    ?.map((x) => ({
                        id: `role-${x.roleId}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.ROLE}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "300px" }}
                                >
                                    {x.roleName}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary mb-0">
                                        {showCeased
                                            ? x?.rolePersonnels?.length
                                            : x?.rolePersonnels?.filter(
                                                  (x) => !x.dateCeased
                                              )?.length}
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [
                                ...createChildNodes(
                                    x.rolePersonnels,
                                    showCeased
                                ),
                            ],
                        },
                    })) ?? [];

            // Initialize tree toggle
            // const expanded = treeNodes.map((z) => z.id);

            // setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        rolePersonnelGrouping,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
        showCeased,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("personnel") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.PersonnelRoleView
                    )
                ) {
                    navigate(
                        `/personnel/${id.split("-personnel-")[1]}/details`
                    );
                }
                if (id.includes("role")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, permissions, rolePersonnelGrouping]
    );

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Personnel by Roles</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                            hideActiveToggle={true}
                        />
                        <ToggleButton
                            id="toggle-button-ceased"
                            checked={showCeased}
                            onChange={onChange}
                            labelA="Hide ceased personnel"
                            labelB="Show ceased personnel"
                            style={{ minWidth: "200px" }}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.PersonnelCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/personnel/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showPersonnelRoles ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showPersonnelRoles ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no personnel roles
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        permissions,
        rolePersonnelGrouping,
        showPersonnelRoles,
        treeData,
        handleToggle,
        toggleState,
        showCeased,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.PersonnelRoleView}
            loading={loadingPersonnel}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<PersonnelByRolesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        rolePersonnelGrouping: state.personnel.rolePersonnelGroupingsFiltered!,
        formFilter: state.personnel.formFilterByRole,
        loadingPersonnel:
            state.personnel.loadState &&
            state.personnel.loadState.status == RequestState.Pending,
        loadPersonnelSuccess:
            state.personnel.loadState &&
            state.personnel.loadState.status == RequestState.Success,
        loadPersonnelFailure:
            state.personnel.loadState &&
            state.personnel.loadState.status == RequestState.Failure,
        loadPersonnelErrorCode: state.personnel.loadState.errorCode,
        user: state.user.data,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<PersonnelByRolesPageProps> => {
    return {
        loadPersonnelByRole: bindActionCreators(
            PersonnelActions.LoadPersonnelByRole,
            dispatch
        ),
        clearPersonnel: bindActionCreators(PersonnelActions.Clear, dispatch),
        filter: bindActionCreators(PersonnelActions.FilterByRole, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(PersonnelByRolesPage);
